import { apiBaseUrl, apiClient } from '@/services/apiClient';
import { clearAccessToken, getAccessToken, getSessionIdentityGeneration, setAccessToken } from '@/services/authTokenStorage';
import { getSessionOriginKey } from '@/services/sessionOrigin';
import {
  abandonSilentRefresh,
  refreshAccessTokenOnce,
  settleSilentRefresh,
  type RefreshedAuthTokens,
} from '@/services/sessionRefresh';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  title: string | null;
  tenantId: string;
  organizationId: string | null;
  officeId: string | null;
  version?: number;
}

export interface SessionTenant {
  id: string;
  name: string;
  slug: string;
}

export interface SessionState {
  authenticated: boolean;
  user: SessionUser | null;
  tenant: SessionTenant | null;
  roles: string[];
  permissions: string[];
  message: string | null;
  sessionId?: string;
  authVersion?: number;
  accessMode?: 'FULL' | 'PASSWORD_CHANGE_REQUIRED' | 'COMPANY_SETUP';
  temporaryPasswordExpiresAt?: string | null;
}

type AuthTokenResponse = RefreshedAuthTokens;

interface AuthSessionResponse {
  authenticated: true;
  user: SessionUser;
  tenant: SessionTenant;
  roles: string[];
  permissions: string[];
  sessionId?: string;
  authVersion?: number;
  accessMode?: 'FULL' | 'PASSWORD_CHANGE_REQUIRED' | 'COMPANY_SETUP';
  temporaryPasswordExpiresAt?: string | null;
}

interface PasskeyOptionsResponse {
  challengeId: string;
  options: unknown;
}

export interface PasskeyEnrollmentResult {
  registered: boolean;
  message: string | null;
}

export interface DemoAccess {
  enabled: boolean;
  externalUrl: string | null;
}

export interface WorkspaceOption {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
}

/**
 * Returned instead of a session when one address opens more than one workspace.
 * No token is issued yet — the caller re-submits with the chosen slug.
 */
export interface WorkspaceSelection {
  requiresWorkspaceSelection: true;
  workspaces: WorkspaceOption[];
}

export type LoginOutcome = SessionState | WorkspaceSelection;

export function isWorkspaceSelection(outcome: LoginOutcome): outcome is WorkspaceSelection {
  return 'requiresWorkspaceSelection' in outcome;
}

export interface EnvironmentOption {
  key: 'production' | 'demo';
  label: string;
  url: string;
  current: boolean;
}

export interface AuthEnvironments {
  current: EnvironmentOption['key'] | null;
  environments: EnvironmentOption[];
}

const unauthenticated = (message: string | null = null): SessionState => ({
  authenticated: false,
  user: null,
  tenant: null,
  roles: [],
  permissions: [],
  message,
});

let authenticationOperation = 0;
function beginAuthenticationOperation() {
  const operation = ++authenticationOperation;
  const generation = getSessionIdentityGeneration();
  return () => operation === authenticationOperation && generation === getSessionIdentityGeneration();
}
const supersededAuthentication = () => unauthenticated('Your sign-in changed. Continue with the current session.');

async function login(input: {
  email: string;
  password: string;
  tenantSlug?: string;
}): Promise<LoginOutcome> {
  const current = beginAuthenticationOperation();
  abandonSilentRefresh();
  await settleSilentRefresh();
  if (!current()) return supersededAuthentication();
  const result = await apiClient.post<AuthTokenResponse | WorkspaceSelection, typeof input>(
    `${apiBaseUrl}/auth/login`,
    input,
  );

  if (!current()) return supersededAuthentication();

  if (!result.ok || !result.data) {
    clearAccessToken();
    return unauthenticated(result.error ?? 'Sign-in failed');
  }

  // Credentials were accepted but they open several workspaces. Nothing is
  // stored yet — the page asks which one and submits again with the slug.
  if ('requiresWorkspaceSelection' in result.data) {
    clearAccessToken();
    return result.data;
  }

  setAccessToken(result.data.accessToken);
  return toSession(result.data);
}

async function getDemoAccess(): Promise<DemoAccess | null> {
  const result = await apiClient.get<DemoAccess>(`${apiBaseUrl}/auth/demo-access`);
  return result.ok && result.data ? result.data : null;
}

async function getEnvironments(): Promise<AuthEnvironments | null> {
  const result = await apiClient.get<AuthEnvironments>(`${apiBaseUrl}/auth/environments`);
  return result.ok && result.data ? result.data : null;
}

async function loginDemo(): Promise<SessionState> {
  const current = beginAuthenticationOperation();
  abandonSilentRefresh();
  await settleSilentRefresh();
  if (!current()) return supersededAuthentication();
  const result = await apiClient.post<AuthTokenResponse>(`${apiBaseUrl}/auth/demo`);
  if (!current()) return supersededAuthentication();
  if (!result.ok || !result.data) {
    clearAccessToken();
    return unauthenticated(result.error ?? 'Demo workspace is unavailable');
  }
  setAccessToken(result.data.accessToken);
  return toSession(result.data);
}

async function loginWithPasskey(email: string): Promise<SessionState> {
  const current = beginAuthenticationOperation();
  abandonSilentRefresh();
  await settleSilentRefresh();
  if (!current()) return supersededAuthentication();
  const optionsResult = await apiClient.post<PasskeyOptionsResponse, { email: string }>(
    `${apiBaseUrl}/auth/passkeys/authentication/options`,
    { email },
  );

  if (!current()) return supersededAuthentication();

  if (!optionsResult.ok || !optionsResult.data) {
    clearAccessToken();
    return unauthenticated(optionsResult.error ?? 'Passkey sign-in is unavailable');
  }

  try {
    const { startAuthentication } = await import('@simplewebauthn/browser');
    const response = await startAuthentication({
      optionsJSON: optionsResult.data.options as Parameters<
        typeof startAuthentication
      >[0]['optionsJSON'],
    });
    if (!current()) return supersededAuthentication();
    const verification = await apiClient.post<
      AuthTokenResponse,
      { challengeId: string; response: typeof response }
    >(`${apiBaseUrl}/auth/passkeys/authentication/verify`, {
      challengeId: optionsResult.data.challengeId,
      response,
    });

    if (!current()) return supersededAuthentication();

    if (!verification.ok || !verification.data) {
      clearAccessToken();
      return unauthenticated(verification.error ?? 'Passkey sign-in failed');
    }

    setAccessToken(verification.data.accessToken);
    return toSession(verification.data);
  } catch (error) {
    if (!current()) return supersededAuthentication();
    clearAccessToken();
    return unauthenticated(passkeyErrorMessage(error));
  }
}

async function enrollPasskey(): Promise<PasskeyEnrollmentResult> {
  const optionsResult = await apiClient.post<PasskeyOptionsResponse>(
    `${apiBaseUrl}/auth/passkeys/registration/options`,
  );
  if (!optionsResult.ok || !optionsResult.data) {
    return {
      registered: false,
      message: optionsResult.error ?? 'Passkey setup is unavailable',
    };
  }

  try {
    const { startRegistration } = await import('@simplewebauthn/browser');
    const response = await startRegistration({
      optionsJSON: optionsResult.data.options as Parameters<
        typeof startRegistration
      >[0]['optionsJSON'],
    });
    const verification = await apiClient.post<
      { verified: true },
      { challengeId: string; response: typeof response }
    >(`${apiBaseUrl}/auth/passkeys/registration/verify`, {
      challengeId: optionsResult.data.challengeId,
      response,
    });

    return verification.ok && verification.data?.verified
      ? { registered: true, message: null }
      : {
          registered: false,
          message: verification.error ?? 'Passkey setup failed',
        };
  } catch (error) {
    return { registered: false, message: passkeyErrorMessage(error) };
  }
}

async function refreshSession(): Promise<SessionState> {
  const result = await refreshAccessTokenOnce();
  if (!result.ok || !result.data) {
    return unauthenticated();
  }
  return toSession(result.data);
}

async function getSession(): Promise<SessionState> {
  if (!getAccessToken()) {
    return refreshSession();
  }

  const origin = getSessionOriginKey();
  const result = await apiClient.get<AuthSessionResponse>(`${apiBaseUrl}/auth/session`,
    origin === null ? undefined : { sessionOrigin: origin });
  if (!result.ok || !result.data) {
    return refreshSession();
  }
  return toSession(result.data);
}

async function changePassword(input: { newPassword: string; currentPassword?: string }): Promise<SessionState> {
  const current = beginAuthenticationOperation();
  const result = await apiClient.post<AuthTokenResponse>(`${apiBaseUrl}/auth/password/change`, input, {
    sessionOrigin: getSessionOriginKey(),
  });
  if (!current()) throw new Error('Your sign-in changed. Review the current session before continuing.');
  if (!result.ok || !result.data) throw new Error(result.error ?? 'Password change failed');
  setAccessToken(result.data.accessToken);
  return toSession(result.data);
}

async function logout(): Promise<void> {
  const current = beginAuthenticationOperation();
  const origin = getSessionOriginKey();
  abandonSilentRefresh();
  await settleSilentRefresh();
  if (!current()) return;
  await apiClient.post<{ revoked: true }>(`${apiBaseUrl}/auth/logout`, undefined,
    origin === null ? undefined : { sessionOrigin: origin });
  if (current()) clearAccessToken();
}

function toSession(data: AuthTokenResponse | AuthSessionResponse): SessionState {
  return {
    authenticated: true,
    user: data.user,
    tenant: data.tenant,
    roles: data.roles,
    permissions: data.permissions,
    message: null,
    sessionId: data.sessionId,
    authVersion: data.authVersion,
    accessMode: data.accessMode ?? 'FULL',
    temporaryPasswordExpiresAt: data.temporaryPasswordExpiresAt,
  };
}

function passkeyErrorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return 'Passkey request was cancelled or timed out';
  }
  if (error instanceof Error && error.name === 'InvalidStateError') {
    return 'This passkey is already registered';
  }
  return 'Your passkey could not be verified. Use your password or try again.';
}

export const authClient = {
  login,
  getDemoAccess,
  getEnvironments,
  loginDemo,
  loginWithPasskey,
  enrollPasskey,
  refreshSession,
  getSession,
  logout,
  changePassword,
};
